import { useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  height?: 'auto' | 'fixed';
  maxHeight?: string;
  showHandle?: boolean;
  showCloseButton?: boolean;
  mobileFullScreen?: boolean;
}

let bodyScrollLockCount = 0;

function lockBodyScroll() {
  bodyScrollLockCount += 1;
  document.body.style.overflow = 'hidden';
}

function unlockBodyScroll() {
  bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
  if (bodyScrollLockCount === 0) {
    document.body.style.overflow = '';
  }
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  footer,
  height = 'auto',
  maxHeight = '70vh',
  showHandle = true,
  showCloseButton = true,
  mobileFullScreen = false,
}: BottomSheetProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const hasBodyScrollLock = useRef(false);

  // Open animation
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (isOpen) {
      setIsVisible(true);
      // Wait for next frame to trigger animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      if (!hasBodyScrollLock.current) {
        lockBodyScroll();
        hasBodyScrollLock.current = true;
      }
    } else if (isVisible) {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      if (hasBodyScrollLock.current) {
        unlockBodyScroll();
        hasBodyScrollLock.current = false;
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      if (hasBodyScrollLock.current) {
        unlockBodyScroll();
        hasBodyScrollLock.current = false;
      }
    };
  }, [isOpen, isVisible]);

  // Handle escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isVisible) return null;

  const heightClass = height === 'fixed'
    ? mobileFullScreen
      ? 'h-[100dvh] md:h-[92dvh]'
      : 'h-[92dvh]'
    : '';
  const maxHeightStyle = height === 'auto' ? { maxHeight } : undefined;
  const cornerClass = mobileFullScreen ? 'rounded-none md:rounded-t-[28px]' : 'rounded-t-[28px]';

  return createPortal(
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 bg-black/50 backdrop-blur-sm
          transition-opacity duration-300
          ${isAnimating ? 'opacity-100' : 'opacity-0'}
        `}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Bottom Sheet */}
      <div
        className={`
          fixed inset-x-0 bottom-0 w-full bg-[#F7F4EF] shadow-2xl
          flex flex-col overflow-hidden
          transition-transform duration-300 ease-out
          ${isAnimating ? 'translate-y-0' : 'translate-y-full'}
          md:left-1/2 md:right-auto md:w-[min(720px,calc(100vw-2rem))] md:-translate-x-1/2
          ${cornerClass}
          ${heightClass}
        `}
        style={maxHeightStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'bottom-sheet-title' : undefined}
      >
        {/* Drag Handle */}
        {showHandle && (
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
            <div className="h-1 w-9 rounded-full bg-[#DDD]" />
          </div>
        )}

        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between border-b border-[#EDEBE6] bg-white px-4 py-3 flex-shrink-0">
            {showCloseButton && (
              <button
                onClick={onClose}
                className="p-2 -m-2 text-neutral-600 transition hover:text-copper"
                aria-label="閉じる"
              >
                <X className="w-5 h-5" />
              </button>
            )}
            {title && (
              <h2 id="bottom-sheet-title" className="flex-1 text-center font-bold text-luxury-dark">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <div className="w-5" /> // Spacer for centering title
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="safe-area-bottom flex-shrink-0">{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
}
